import { motion } from "framer-motion";
import { Wrench, Disc3, Activity, CircleDot, Zap, ArrowUpRight } from "lucide-react";
import { EASE, Reveal, MonoLabel } from "./shared";

const SERVICES = [
  {
    num: "01",
    title: "Preventive Maintenance",
    icon: Wrench,
    copy: "Oil and filter changes, fluid exchanges, tune-ups and factory-schedule service that stop small issues from becoming expensive ones.",
    span: "md:col-span-7",
    img: "/photos/oil-change-1-web.jpg",
  },
  {
    num: "02",
    title: "Brake Service & Repair",
    icon: Disc3,
    copy: "Pads, rotors, calipers, lines and fluid — from a squeal to a full rebuild, done right the first time.",
    span: "md:col-span-5",
    img: "/photos/brake-job-web.jpg",
  },
  {
    num: "03",
    title: "Engine Diagnostics & Repair",
    icon: Activity,
    copy: "Check-engine lights, computerized diagnostics, ignition, timing and complete engine repair.",
    span: "md:col-span-4",
    img: "/photos/engine-repair-web.jpg",
  },
  {
    num: "04",
    title: "Suspension, Steering & Tires",
    icon: CircleDot,
    copy: "Struts, shocks, ball joints, alignments and tire service for a smooth, straight ride.",
    span: "md:col-span-4",
    img: "/photos/struts-web.jpg",
  },
  {
    num: "05",
    title: "Electrical & Battery",
    icon: Zap,
    copy: "Batteries, alternators, starters, sensors and wiring gremlins — tracked down and fixed.",
    span: "md:col-span-4",
    img: "/photos/electrical-web.jpg",
  },
];

function Card({ s, i }) {
  const Icon = s.icon;
  return (
    <motion.article
      data-testid={`service-card-${s.num}`}
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: i * 0.07, ease: EASE }}
      className={`group relative flex flex-col border border-line bg-panel p-7 transition-colors duration-300 hover:border-gold/70 md:p-9 ${s.span}`}
    >
      <div className="flex items-start justify-between">
        <span className="font-mono text-5xl text-zinc-600 transition-colors duration-300 group-hover:text-gold md:text-6xl">
          {s.num}
        </span>
        <span className="flex items-center gap-3">
          <Icon
            size={22}
            strokeWidth={1.6}
            className="text-zinc-400 transition-colors duration-300 group-hover:text-gold"
          />
          <ArrowUpRight
            size={18}
            className="translate-y-1 text-gold opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100"
          />
        </span>
      </div>
      <h3 className="mt-8 font-display text-2xl uppercase tracking-wide text-zinc-100 md:text-3xl">
        {s.title}
      </h3>
      <p className="mt-3 max-w-md text-sm leading-relaxed text-zinc-300">
        {s.copy}
      </p>
      {s.img && (
        <div className="relative mt-7 h-44 overflow-hidden border border-line bg-void md:h-48">
          <img
            src={s.img}
            alt={`${s.title} at His Grace Auto Repair`}
            loading="lazy"
            decoding="async"
            width={900}
            height={1200}
            className="block h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-[1.03] group-hover:grayscale-0"
          />
        </div>
      )}
    </motion.article>
  );
}

export default function Services() {
  return (
    <section id="services" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8 lg:px-14">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <MonoLabel>Manifesto — What We Fix</MonoLabel>
            <h2 className="mt-4 font-display text-5xl uppercase leading-[0.9] tracking-wide sm:text-6xl lg:text-7xl">
              Service <span className="text-gold">Chapters</span>
            </h2>
          </div>
          <p className="max-w-xs pb-2 text-sm leading-relaxed text-zinc-300">
            Five disciplines, one standard: diagnose it properly, explain it
            plainly, fix it once.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-12">
          {SERVICES.map((s, i) => (
            <Card key={s.num} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
